<template>
  <GMapMap
    v-if="mapCenter"
    :center="mapCenter"
    :zoom="16"
    map-type-id="terrain"
    :options="mapOptions"
    ref="gmap"
  >
  </GMapMap>
</template>

<script>
import pinIcon from '@/assets/images/pin.svg';
import unpinIcon from '@/assets/images/unpin.svg';

export default {
  name: 'GoogleMaps',
  emits: ['map-idle'],
  data() {
    return {
      center: null,
      selectedMarkerIndex: null,
      mapIdleListener: null,
      mapOptions: {
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        zoomControl: true,
        minZoom: 8,
        gestureHandling: 'greedy',
        mapId: process.env.VUE_APP_GOOGLE_MAPS_MAP_ID,
      },
      mapInstance: null,
      mapMarkers: [],
    }
  },
  computed: {
    doctorsWithPosition() {
      return this.doctors
        .map((doctor, originalIndex) => {
          if (!doctor || !doctor.position) {
            return null;
          }
          const hasCoords = typeof doctor.position.lat === 'number'
            && typeof doctor.position.lng === 'number';
          if (!hasCoords) {
            return null;
          }
          return { doctor, originalIndex };
        })
        .filter(Boolean);
    },
    mapCenter() {
      if (this.center && typeof this.center.lat === 'number' && typeof this.center.lng === 'number') {
        return this.center;
      }
      const firstWithPosition = this.doctorsWithPosition[0];
      if (firstWithPosition && firstWithPosition.doctor && firstWithPosition.doctor.position) {
        return firstWithPosition.doctor.position;
      }
      return null;
    }
  },
  props: {
    doctors: {
      type: Array,
      required: true,
    },
    selectedDoctor: {
      type: Object,
      default: null
    }
  },
  watch: {
    doctors: {
      handler() {
        this.refreshMarkers();
      },
      deep: true,
    },
    selectedMarkerIndex() {
      this.updateMarkerIcons();
    },
  },
  mounted() {
    const initialCenter = this.mapCenter;
    if (initialCenter) {
      this.center = initialCenter;
      this.panTo(initialCenter);
    }
    this.refreshMarkers();
  },
  methods: {
    onMarkerClick(index) {
      const entry = this.doctorsWithPosition[index];
      if (!entry || !entry.doctor?.position) {
        return;
      }

      this.center = entry.doctor.position;
      this.selectedMarkerIndex = index;
      this.panTo(entry.doctor.position);

      const doctorElement = document.getElementById(`doctor-${entry.originalIndex}`);
      if (doctorElement) {
        doctorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    focusDoctor(doctor) {
      if (!doctor) {
        return;
      }
      const targetIndex = this.doctorsWithPosition.findIndex((entry) => entry.doctor === doctor);
      if (targetIndex === -1) {
        return;
      }
      this.onMarkerClick(targetIndex);
    },

    getMap() {
      if (this.mapInstance) {
        return Promise.resolve(this.mapInstance);
      }
      const mapRef = this.$refs.gmap;
      if (!mapRef || !mapRef.$mapPromise) {
        return Promise.resolve(null);
      }
      return mapRef.$mapPromise.then((map) => {
        this.mapInstance = map;
        this.setupMapListeners(map);
        return map;
      });
    },
    setupMapListeners(map) {
      if (!map || this.mapIdleListener || typeof map.addListener !== 'function') {
        return;
      }
      this.mapIdleListener = map.addListener('idle', () => {
        this.emitMapIdle();
      });
      this.emitMapIdle();
    },
    emitMapIdle() {
      const state = this.getMapState();
      if (state) {
        this.$emit('map-idle', state);
      }
    },
    getMapState() {
      const map = this.mapInstance;
      if (!map || typeof map.getCenter !== 'function' || typeof map.getBounds !== 'function') {
        return null;
      }
      const center = map.getCenter();
      const bounds = map.getBounds();
      if (!center || !bounds) {
        return null;
      }
      const ne = bounds.getNorthEast ? bounds.getNorthEast() : null;
      const sw = bounds.getSouthWest ? bounds.getSouthWest() : null;
      if (!ne || !sw || typeof ne.lat !== 'function' || typeof sw.lat !== 'function') {
        return null;
      }
      return {
        center: { lat: center.lat(), lng: center.lng() },
        bounds: {
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
        },
        zoom: typeof map.getZoom === 'function' ? map.getZoom() : null,
      };
    },

    refreshMarkers() {
      this.getMap().then((map) => {
        if (!map) {
          return;
        }
        this.clearMarkers();
        this.mapMarkers = this.doctorsWithPosition.map((entry, index) => {
          return this.createMarker(map, entry, index);
        });
        this.updateMarkerIcons();
      });
    },

    clearMarkers() {
      this.mapMarkers.forEach((entry) => {
        this.removeMarker(entry.marker);
      });
      this.mapMarkers = [];
    },

    removeMarker(marker) {
      if (!marker) {
        return;
      }
      if (typeof marker.setMap === 'function') {
        marker.setMap(null);
        return;
      }
      marker.map = null;
    },

    createMarker(map, entry, index) {
      const position = entry.doctor.position;
      const iconUrl = this.getMarkerIcon(index);
      const google = window.google;
      const mapId = map && typeof map.get === 'function' ? map.get('mapId') : null;
      if (mapId && google && google.maps && google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        const content = this.buildMarkerContent(iconUrl);
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position,
          content,
        });
        marker.isAdvanced = true;
        marker.addListener('gmp-click', () => this.onMarkerClick(index));
        return { marker, content, index };
      }
      const marker = new google.maps.Marker({
        map,
        position,
        clickable: true,
        draggable: false,
        icon: { url: iconUrl },
      });
      marker.addListener('click', () => this.onMarkerClick(index));
      return { marker, index };
    },

    buildMarkerContent(iconUrl) {
      const img = document.createElement('img');
      img.src = iconUrl;
      img.alt = '';
      img.style.transform = 'translate(-50%, -100%)';
      return img;
    },

    panTo(position) {
      if (!position) {
        return;
      }
      this.getMap().then((map) => {
        if (map && typeof map.panTo === 'function') {
          map.panTo(position);
        }
      });
    },

    updateMarkerIcons() {
      if (!this.mapMarkers.length) {
        return;
      }
      this.mapMarkers.forEach((entry) => {
        const iconUrl = this.getMarkerIcon(entry.index);
        if (entry.marker && entry.marker.isAdvanced && entry.content) {
          entry.content.src = iconUrl;
          return;
        }
        if (entry.marker && typeof entry.marker.setIcon === 'function') {
          entry.marker.setIcon({ url: iconUrl });
        }
      });
    },

    getMarkerIcon(index) {
      if (index === this.selectedMarkerIndex) {
        return pinIcon;
      }
      return unpinIcon;
    }
  }
}
</script>
